import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import {
  RequestLeave,
  RequestLeaveStateChange,
  RequestLeaveStatus,
  changeRequestLeaveState,
  getRequestLeave,
} from '@/services/RequestLeavesService'; // Import your service
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/Breadcrumb';
import Spinner from '@/components/Spinner';
import DefaultLayout from '@/layout/DefaultLayout';
import Input from '@/components/Input';
import TextArea from '@/components/TextArea';
import {
  RequestLeaveState,
  mapBackendStateToFrontendState,
  stateColors,
} from '@/enums/RequestLeaveState';
import { EditButton, DeleteButton } from '@/components/shared/Button';
import { setLoading } from '@/state/slices/employeeSlice';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import { useAuth } from '@/context/AuthContext';
import { UserRoles } from '@/enums/UsersRole';

function RequestLeaveDetails() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [commentText, setCommentText] = useState('');
  const { user } = useAuth();
  const {
    data: requestLeaveDetail,
    isLoading,
    refetch,
    isSuccess,
  } = useQuery<RequestLeave | undefined>(
    ['requestLeave', id],
    async () => await getRequestLeave(id!),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isSuccess && requestLeaveDetail) {
      setCommentText(requestLeaveDetail?.comment ?? '');
    }
  }, [isSuccess, requestLeaveDetail]);

  const [loading, setLoading] = useState(false);

  const handleCancelReject = () => {
    setIsModalVisible(false);
  };

  const handleRejectConfirmation = async () => {
    // !!!handle reject request
    setLoading(true);
    const reqState: RequestLeaveStateChange = {
      requestLeaveId: id!,
      state: RequestLeaveState.Rejected,
      comment: commentText,
    };
    const data = await changeRequestLeaveState(reqState);
    setLoading(false);
    setIsModalVisible(false);
    refetch();
  };

  const handleApproveRequest = async () => {
    // !!!handle approve request
    setLoading(true);
    const reqState: RequestLeaveStateChange = {
      requestLeaveId: id!,
      state: RequestLeaveState.Approved,
      comment: commentText,
    };
    const data = await changeRequestLeaveState(reqState);
    setLoading(false);
    setIsModalVisible(false);
    refetch();
  };
  const ActionButtonsCell = () => {
    return (
      <div className="flex items-end justify-end">
        <EditButton onClick={() => handleApproveRequest()}>
          {t('Requestleave.Approve')}
        </EditButton>

        <DeleteButton
          type="button"
          onClick={() => {
            setIsModalVisible(true);
          }}
        >
          {t('Requestleave.Reject')}
        </DeleteButton>
      </div>
    );
  };

  const getColorizedState = (state: string) => {
    const frontendState = RequestLeaveState[state];
    const bgColor = stateColors[frontendState] || 'bg-gray-300';

    return (
      <div
        className={`rounded-md text-center ${bgColor} px-2.5  py-2.5 text-sm text-white`}
      >
        {frontendState !== null ? t(`RequestLeaveState.${frontendState}`) : ''}
      </div>
    );
  };
  return isLoading || loading ? (
    <Spinner />
  ) : (
    <DefaultLayout>
      <Breadcrumb pageName={t('RequestLeaves.detailsPage')} />
      <div className="p-8">
        {isModalVisible && (
          <DeleteConfirmationModal
            isOpen={isModalVisible}
            onDelete={handleRejectConfirmation}
            onCancel={handleCancelReject}
            textContent={t('doyouwantToRejectThisElement')}
          />
        )}
        <div className="group relative z-0 mb-6 w-30">
          {getColorizedState(requestLeaveDetail?.state!)}
        </div>
        <div className="grid md:grid-cols-2 md:gap-6">
          <div className="group relative z-0 mb-6 w-full">
            <Input
              name="fullNameColumn"
              type="text"
              value={requestLeaveDetail?.employeeFullName}
              label={t('RequestLeaves.fullNameColumn')}
              readOnly
            />
          </div>
          <div className="group relative z-0 mb-6 w-full">
            <Input
              name="title"
              type="text"
              value={requestLeaveDetail?.vacationName}
              label={t('RequestLeaves.title')}
              readOnly
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 md:gap-6">
          <div className="group relative z-0 mb-6 w-full">
            <Input
              name="start"
              value={moment(requestLeaveDetail?.fromDate).format('L')}
              label={t('Requestleave.startDate')}
              readOnly
            />
          </div>
          <div className="group relative z-0 mb-6 w-full">
            <Input
              name="end"
              value={moment(requestLeaveDetail?.toDate).format('L')}
              label={t('Requestleave.endDate')}
              readOnly
            />
          </div>
        </div>
        <div className="grid md:grid-cols-2 md:gap-6">
          <div className="group relative z-0 mb-6 w-full">
            <TextArea
              name="comment"
              id="comment"
              value={commentText}
              label={t('RequestLeaves.comment')}
              onChange={(e) => setCommentText(e.target.value)}
              readOnly={
                user?.isTeamLeader != 'True' ||
                requestLeaveDetail?.state !=
                  RequestLeaveState.Pending.toString()
              }
            />
          </div>
          <div className="group relative z-0 mb-6 w-full">
            <TextArea
              name="note"
              value={requestLeaveDetail?.note}
              label={t('RequestLeaves.note')}
              readOnly
            />
          </div>
        </div>
        {requestLeaveDetail?.state! == RequestLeaveState.Pending.toString() && (
          <ActionButtonsCell />
        )}
        {/* Display other properties */}
      </div>
    </DefaultLayout>
  );
}

export default RequestLeaveDetails;
